import { useEffect, useState } from "react";
import { parseQueryString, splitUrlParts, type ParsedParam, type ParsedQuery } from "@shared/parse-query";
import { readBrowserInfo, type BrowserInfo } from "./browser-info";

/**
 * Everything rendered below comes from untrusted input (the query string a desktop app built).
 * It is rendered exclusively as React children -- never as dangerouslySetInnerHTML -- so a value
 * such as <script>alert(1)</script> shows up as those literal characters.
 */

interface ServerEcho {
  server: {
    originalUrl: string;
    path: string;
    rawQuery: string | null;
    method: string;
    host: string | null;
    userAgent: string | null;
    receivedAt: string;
  };
  parsed: ParsedQuery;
}

export default function App() {
  // Read from window.location, not from a router: this is what the browser actually holds.
  const href = safeRead(() => window.location.href, "");
  const pathname = safeRead(() => window.location.pathname, "");
  const hash = safeRead(() => window.location.hash, "");

  // Split the query out of href by hand rather than using location.search, which reports ""
  // for BOTH "/page" and "/page?" -- and telling those two apart is exactly the kind of
  // detail this page exists to report.
  const rawQuery = splitUrlParts(href).query;
  const search = rawQuery === null ? "" : `?${rawQuery}`;

  const parsed = parseQueryString(search);
  const browser = safeRead(readBrowserInfo, EMPTY_BROWSER);

  return (
    <main>
      <header className="page-head">
        <h1>skyCare Query Parameter Validation</h1>
        <p className="lede">
          Validates that a link launched from the skyCare desktop app delivers its query parameters
          intact to the browser. Nothing below is normalised or repaired -- the raw text is the
          evidence.
        </p>
      </header>

      <RawQuerySection parsed={parsed} />
      <ParamsSection parsed={parsed} />
      <LocationSection href={href} pathname={pathname} search={search} hash={hash} />
      <BrowserSection browser={browser} />
      <ServerSection clientRawQuery={parsed.raw} search={search} />
    </main>
  );
}

/* --- 1. the raw query string, verbatim ------------------------------------------------------ */

function RawQuerySection({ parsed }: { parsed: ParsedQuery }) {
  return (
    <section>
      <h2>1. Raw query string</h2>
      {parsed.hadQuestionMark && parsed.isEmpty ? (
        <p className="empty-state">
          The URL ended with a bare <code>?</code> and nothing after it. The query string is empty.
        </p>
      ) : parsed.isEmpty ? (
        <p className="empty-state">No query string was sent. The URL had no <code>?</code> at all.</p>
      ) : (
        <>
          <pre className="raw-block">{parsed.raw}</pre>
          <p className="meta">
            {parsed.raw.length} characters, everything after the first <code>?</code>, shown verbatim.{" "}
            <CopyButton text={parsed.raw} />
          </p>
        </>
      )}
      <NoteList notes={parsed.notes} />
    </section>
  );
}

/* --- 2. split into parameters ---------------------------------------------------------------- */

function ParamsSection({ parsed }: { parsed: ParsedQuery }) {
  return (
    <section>
      <h2>2. Parameters</h2>
      {parsed.params.length === 0 ? (
        <p className="empty-state">Nothing to split -- there are no parameters.</p>
      ) : (
        <>
          <p className="meta">
            {parsed.params.length} segment{parsed.params.length === 1 ? "" : "s"} separated by{" "}
            <code>&amp;</code>, in arrival order. Duplicates and empty segments are kept, not merged.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="col-num">#</th>
                  <th>Key</th>
                  <th>Value (raw)</th>
                  <th>Value (decoded)</th>
                </tr>
              </thead>
              <tbody>
                {parsed.params.map((p) => (
                  <ParamRow key={p.position} param={p} isDuplicate={parsed.duplicateKeys.includes(p.key)} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function ParamRow({ param, isDuplicate }: { param: ParsedParam; isDuplicate: boolean }) {
  const empty = param.rawSegment === "";
  return (
    <tr className={empty ? "row-empty" : undefined}>
      <td className="col-num">{param.position}</td>
      <td>
        {empty ? (
          <span className="placeholder">(empty segment)</span>
        ) : (
          <>
            <code className="key">{param.key}</code>
            {isDuplicate && <span className="badge badge-dup">duplicate key</span>}
            {param.keyDecodeError && <span className="badge badge-bad">could not decode</span>}
            {param.rawKey !== param.key && (
              <div className="sub">
                raw: <code>{param.rawKey}</code>
              </div>
            )}
          </>
        )}
        <NoteList notes={param.notes} inline />
      </td>
      <td>
        <ValueCell value={param.rawValue} />
      </td>
      <td>
        {param.valueDecodeError ? (
          <>
            <span className="badge badge-bad">could not decode</span>
            <ValueCell value={param.rawValue} />
            <div className="sub">Showing the raw, undecoded value.</div>
          </>
        ) : (
          <ValueCell value={param.value} />
        )}
      </td>
    </tr>
  );
}

function ValueCell({ value }: { value: string | null }) {
  if (value === null) return <span className="placeholder">(no value)</span>;
  if (value === "") return <span className="placeholder">(empty string)</span>;
  return (
    <>
      <code className="value">{value}</code>
      {value.length > 80 && <div className="sub">{value.length} characters</div>}
    </>
  );
}

/* --- 3. location ----------------------------------------------------------------------------- */

function LocationSection(props: { href: string; pathname: string; search: string; hash: string }) {
  return (
    <section>
      <h2>3. URL as the browser sees it</h2>
      <dl className="facts">
        <Fact label="Full URL" value={props.href} />
        <Fact label="Path" value={props.pathname} />
        <Fact label="Query (with ?)" value={props.search} emptyText="(none)" />
        <Fact
          label="Fragment"
          value={props.hash}
          emptyText="(none)"
          note="A fragment is never sent to the server; only the browser sees it."
        />
      </dl>
    </section>
  );
}

/* --- 4. browser ------------------------------------------------------------------------------ */

function BrowserSection({ browser }: { browser: BrowserInfo }) {
  const friendly =
    browser.name && browser.version
      ? `${browser.name} ${browser.version}`
      : browser.name
        ? browser.name
        : "Could not be determined";

  return (
    <section>
      <h2>4. Browser</h2>
      <dl className="facts">
        <Fact
          label="Browser"
          value={friendly}
          note={
            browser.source === "userAgentData"
              ? "From navigator.userAgentData (structured and authoritative)."
              : browser.source === "userAgent"
                ? "Parsed from navigator.userAgent -- a best-effort guess."
                : "Neither navigator.userAgentData nor a recognised User-Agent pattern matched."
          }
        />
        <Fact label="User agent" value={browser.userAgent} emptyText="(unavailable)" />
        <Fact label="Platform" value={browser.platform ?? ""} emptyText="(unknown)" />
        <Fact
          label="Mobile"
          value={browser.mobile === null ? "" : browser.mobile ? "yes" : "no"}
          emptyText="(unknown)"
        />
        <Fact label="Brands" value={browser.brands.join(" | ")} emptyText="(not exposed by this browser)" />
        <Fact label="Languages" value={browser.languages.join(", ")} emptyText="(unknown)" />
      </dl>
    </section>
  );
}

/* --- 5. what the server received -------------------------------------------------------------- */

/**
 * Secondary evidence: the same request as the Node process saw it on the wire. If this disagrees
 * with section 1, something between the desktop app and the server rewrote the URL.
 */
function ServerSection({ clientRawQuery, search }: { clientRawQuery: string; search: string }) {
  const [state, setState] = useState<{ status: "loading" | "ok" | "failed"; echo?: ServerEcho; error?: string }>({
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    // Forward the query string unchanged so the server sees the same request target.
    const target = `/api/echo${search}`;
    (async () => {
      try {
        const res = await fetch(target, { headers: { accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const echo = (await res.json()) as ServerEcho;
        if (!cancelled) setState({ status: "ok", echo });
      } catch (err) {
        if (!cancelled) setState({ status: "failed", error: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search]);

  return (
    <section>
      <h2>5. What the server received</h2>
      {state.status === "loading" && <p className="meta">Asking the server...</p>}
      {state.status === "failed" && (
        <p className="empty-state">
          Could not reach <code>/api/echo</code> ({state.error}). Sections 1-4 above are unaffected --
          they are read straight from the browser.
        </p>
      )}
      {state.status === "ok" && state.echo && (
        <>
          <dl className="facts">
            <Fact label="Request target" value={state.echo.server.originalUrl} />
            <Fact label="Path" value={state.echo.server.path} />
            <Fact label="Raw query" value={state.echo.server.rawQuery ?? ""} emptyText="(none)" />
            <Fact label="Host header" value={state.echo.server.host ?? ""} emptyText="(none)" />
            <Fact label="Received at" value={state.echo.server.receivedAt} />
          </dl>
          {state.echo.server.rawQuery !== null && state.echo.server.rawQuery !== clientRawQuery && (
            <p className="warn">
              The server's raw query string differs from the browser's. Something rewrote the URL in
              transit -- compare section 1 against the value above.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/* --- small shared pieces ---------------------------------------------------------------------- */

function Fact({
  label,
  value,
  emptyText,
  note,
}: {
  label: string;
  value: string;
  emptyText?: string;
  note?: string;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {value === "" ? (
          <span className="placeholder">{emptyText ?? "(empty)"}</span>
        ) : (
          <code>{value}</code>
        )}
        {note && <div className="sub">{note}</div>}
      </dd>
    </>
  );
}

function NoteList({ notes, inline }: { notes: string[]; inline?: boolean }) {
  if (notes.length === 0) return null;
  return (
    <ul className={inline ? "notes notes-inline" : "notes"}>
      {notes.map((note, i) => (
        <li key={i}>{note}</li>
      ))}
    </ul>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="copy"
      onClick={() => {
        // Clipboard access is blocked in insecure contexts and can reject; a failed copy must
        // not break the page, so the label simply does not change.
        void safePromise(navigator?.clipboard?.writeText(text)).then((ok) => {
          if (!ok) return;
          setCopied(true);
          setTimeout(() => setCopied(false), 1_500);
        });
      }}
    >
      {copied ? "copied" : "copy"}
    </button>
  );
}

const EMPTY_BROWSER: BrowserInfo = {
  userAgent: "",
  name: null,
  version: null,
  source: "unknown",
  platform: null,
  mobile: null,
  brands: [],
  languages: [],
};

/** Reading window/navigator can throw in odd embeddings; a blank field beats a blank page. */
function safeRead<T>(read: () => T, fallback: T): T {
  try {
    const value = read();
    return value === undefined || value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

async function safePromise(p: Promise<unknown> | undefined): Promise<boolean> {
  try {
    await p;
    return p !== undefined;
  } catch {
    return false;
  }
}
